import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { InvitationInfo } from "../../shared/api-types";
import { PASSWORD_MIN_LENGTH } from "../../shared/password-rules";
import { LangToggle } from "../components/LangToggle";
import { PasswordStrength } from "../components/PasswordStrength";
import { ThemeToggle } from "../components/ThemeToggle";
import { Button, Card, Input } from "../components/ui";
import { apiError, useT } from "../i18n";
import { apiJson } from "../lib/api";
import { authClient } from "../lib/auth";
import { usePasswordCheck } from "../lib/password";
import { LoginPage } from "./LoginPage";

/**
 * Einladungslink (/invite/:id). Funktioniert mit und ohne Session:
 * – neue Person: Name + Passwort → Zugang wird angelegt, danach Auto-Login
 * – bestehender User ohne Session: zur Anmeldung, danach landet er wieder hier
 * – eingeloggt mit passender E-Mail: ein Klick, dann ab in den Account
 * – eingeloggt mit anderer E-Mail: Hinweis + Abmelden
 */
export function InvitePage({ session }: { session: { email: string } | null }) {
  const t = useT();
  const navigate = useNavigate();
  const id = useLocation().pathname.split("/")[2] ?? "";
  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bestehender User: Login inline anzeigen — die URL bleibt /invite/:id, nach
  // dem Login rendert App diese Seite erneut, dann mit Session.
  const [showLogin, setShowLogin] = useState(false);
  const check = usePasswordCheck(password, { email: info?.email, name });

  useEffect(() => {
    apiJson<InvitationInfo>(`/api/invitations/${id}`)
      .then(setInfo)
      .catch(() => setLoadFailed(true));
  }, [id]);

  async function accept(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await apiJson<{ signIn: boolean }>(`/api/invitations/${id}/accept`, {
        method: "POST",
        json: session ? {} : { name, password },
      });
      if (res.signIn && info) {
        const { error: signInError } = await authClient.signIn.email({
          email: info.email,
          password,
        });
        if (signInError) {
          // Zugang existiert jetzt — zur Anmeldung, falls der Auto-Login hakt
          navigate("/", { replace: true });
          return;
        }
      }
      // Sitzung hängt jetzt am neuen Account → komplett neu laden
      window.location.assign("/");
    } catch (err) {
      setError(apiError(t, err instanceof Error ? err.message : "unknown"));
      setBusy(false);
    }
  }

  if (showLogin) return <LoginPage />;

  const mismatch = session !== null && info !== null && session.email.toLowerCase() !== info.email;

  let body;
  if (loadFailed || (info && info.state === "invalid")) {
    body = <p className="text-sm text-red-600 dark:text-red-400">{t.invite.invalid}</p>;
  } else if (!info) {
    body = <p className="text-sm text-zinc-400">{t.common.loading}</p>;
  } else if (info.state === "expired") {
    body = <p className="text-sm text-red-600 dark:text-red-400">{t.invite.expired}</p>;
  } else if (info.state === "used") {
    body = (
      <div className="space-y-4">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{t.invite.used}</p>
        <Button className="w-full" onClick={() => window.location.assign("/")}>
          {t.reset.toLogin}
        </Button>
      </div>
    );
  } else if (mismatch) {
    body = (
      <div className="space-y-4">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          {t.invite.mismatch(session!.email, info.email)}
        </p>
        <Button
          className="w-full"
          variant="ghost"
          onClick={() => void authClient.signOut().then(() => window.location.reload())}
        >
          {t.menu.signOut}
        </Button>
      </div>
    );
  } else if (session) {
    body = (
      <div className="space-y-4">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          {t.invite.acceptAs(session.email)}
        </p>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <Button className="w-full" disabled={busy} onClick={() => void accept()}>
          {t.invite.accept}
        </Button>
      </div>
    );
  } else if (info.userExists) {
    body = (
      <div className="space-y-4">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{t.invite.signInFirst}</p>
        <Button className="w-full" onClick={() => setShowLogin(true)}>
          {t.login.submit}
        </Button>
      </div>
    );
  } else {
    body = (
      <form onSubmit={(e) => void accept(e)} className="space-y-4">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          {t.invite.newUserText(info.email)}
        </p>
        <Input
          className="w-full"
          placeholder={t.invite.namePlaceholder}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />
        <Input
          type="password"
          className="w-full"
          placeholder={t.reset.passwordPlaceholder(PASSWORD_MIN_LENGTH)}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={PASSWORD_MIN_LENGTH}
          required
        />
        <PasswordStrength password={password} result={check} />
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy || !!check?.rejection}>
          {t.invite.createAndAccept}
        </Button>
      </form>
    );
  }

  return (
    <div className="relative grid min-h-dvh place-items-center px-4">
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <LangToggle />
        <ThemeToggle />
      </div>
      <main className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold tracking-tight">
            fast<span className="text-accent">360</span>
          </h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{t.login.tagline}</p>
        </div>
        <Card>
          <h2 className="mb-1 font-semibold">{t.invite.title}</h2>
          {info && info.state === "pending" && (
            <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
              {t.invite.intro(
                info.accountName,
                info.role === "owner" ? t.members.roleOwner : t.members.roleMember,
              )}
            </p>
          )}
          {body}
        </Card>
      </main>
    </div>
  );
}
